import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import moment from "moment";
import "moment/locale/en-gb";
import "moment/locale/es";
import { useNavigate, useParams } from "react-router-dom";
import Arrow from "../../assets/img/arrow-left-grey-round.svg";
import DownloadIcon from "../../assets/img/downloadIcon.svg";
import Loader from "../../components/Loader";
import AuthContext from "../../context/auth/AuthContext";
import LandLordContext from "../../context/landlord/LandLordContext";
import {
  calculateFinalScore,
  getCustomIncomeDetails,
  getFieldForPolicies,
  getIdentityDetails,
  haveValue,
  resolveIncomeSelection,
} from "../../helper/common";
import useTranslator from "../../hooks/useTranslator";
import { computeFinalResult } from "../../Utils/computeFinalResult";
import { SUB_SCORE_TRANSLATIONS } from "../../constants/policyConstants";
import {
  getPillarBadgeFromScore,
  PILLAR_TITLE_KEYS,
} from "../../helper/pillarStatus";
import "../../styles/linked-report-overview.css";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  yellow: {
    backgroundColor: "#FFFBEC",
    color: "#E1BB20",
    border: ".1389vw solid #F2E09B",
  },
  green: {
    backgroundColor: "#F2FBE9",
    color: "#82CF3F",
    border: ".1389vw solid #DAECC8",
  },
  red: {
    backgroundColor: "#FFE2E2",
    color: "#FF3C3C",
    border: ".1389vw solid #EB9B9C",
  },
};

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const fmtCurrency = (n, lang) => {
  if (!n && n !== 0) {
    return lang === "es" ? "Sin datos" : "No data";
  }

  return `€${Number(n).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  })}/mo`;
};

const formatDateOfBirth = (dob, lang) => {
  if (!dob) return lang === "es" ? "Sin datos" : "No data";
  const d = moment(dob);
  return d.isValid()
    ? d.format("DD/MM/YYYY")
    : lang === "es"
      ? "Sin datos"
      : "No data";
};

// Pillar display order — left column / right column
const LEFT_PILLARS = [
  "incomeAffordability",
  "financialBehaviour",
  "documentationStrength",
];
const RIGHT_PILLARS = ["employmentStability", "creditDefaultRisk"];

const PILLAR_TITLES = {
  incomeAffordability: { en: "Affordability", es: "Asequibilidad" },
  employmentStability: {
    en: "Employment stability",
    es: "Estabilidad laboral",
  },
  financialBehaviour: {
    en: "Financial behaviour",
    es: "Comportamiento financiero",
  },
  creditDefaultRisk: { en: "Default risk", es: "Riesgo de impago" },
  documentationStrength: { en: "Documentation", es: "Documentación" },
};

const STATUS_VARIANT_STYLES = {
  success: {
    color: "#82CF3F",
    backgroundColor: "#F2FBE9",
  },
  warning: {
    color: "#E1BB20",
    backgroundColor: "#FFFBEC",
  },
  danger: {
    color: "#FF3C3C",
    backgroundColor: "#FFE2E2",
  },
  neutral: {
    color: "#65727f",
    backgroundColor: "#f1f4f7",
  },
};

// Sub-score keys per pillar (effortRate handled separately for Affordability)
const PILLAR_SUB_KEYS = {
  incomeAffordability: ["incomeVerification", "incomeConsistency"],
  employmentStability: [
    "tenure",
    "employmentPredictability",
    "employerStability",
    "sectorCyclicality",
  ],
  financialBehaviour: ["financialSkor"],
  creditDefaultRisk: ["defaultHistory"],
  documentationStrength: [], // handled manually below
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const CapacityReportPage = () => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  const { user } = useContext(AuthContext);
  const {
    getTenantExtraData,
    deaWorkspace,
    customPolicyDetails,
    getPolicyDetails,
    getSharedProfile,
  } = useContext(LandLordContext);

  // Read shared profile IDs from query string (?sharedprofileid=xxx&sharedprofileid=yyy)
  const sharedProfileIds = useMemo(
    () => new URLSearchParams(window.location.search).getAll("sharedprofileid"),
    []
  );

  // Store shared profile data by tenant ID
  const [sharedProfileMap, setSharedProfileMap] = useState({});

  const currentLang = useMemo(() => {
    const raw = localStorage.getItem("i18nextLng")?.toLowerCase() || "en-us";
    return raw === "en-us" ? "en" : "es";
  }, []);

  const noData = currentLang === "es" ? "Sin datos" : "No data";

  // ── State ─────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState("loading");
  const [localReport, setLocalReport] = useState(null);
  const [showSpinner, setShowSpinner] = useState(false);
  // { [tenantLabel]: { effortRate, income } }
  const [tenantMetaMap, setTenantMetaMap] = useState({});
  // { [guarantorLabel]: { effortRate, income } }
  const [guarantorMetaMap, setGuarantorMetaMap] = useState({});

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getIncomeForRecord = useCallback(
    (record, incomeSelection, customIncomeSources) => {
      const income = getCustomIncomeDetails(
        record,
        user,
        _t,
        incomeSelection,
        undefined,
        undefined,
        deaWorkspace,
        customIncomeSources
      );
      return Number(income?.amount || 0);
    },
    [_t, user, deaWorkspace]
  );

  const getEffortRateForTenant = useCallback(
    (tenant, sharedProfile) => {
      // Check shared profile effort rate data first
      const fromSharedProfile =
        sharedProfile?.effort_rate_data?.tenant?.effort_rate_percentage;
      if (fromSharedProfile != null && !isNaN(Number(fromSharedProfile)))
        return Number(fromSharedProfile);

      // Check workspace default effort rate
      const fromWs = user?.current_workspace?.default_effort_rate;
      if (fromWs != null && !isNaN(Number(fromWs))) return Number(fromWs);

      // Default to 35%
      return 35;
    },
    [user]
  );

  // ── Load data ─────────────────────────────────────────────────────────────
  useEffect(() => {
    getPolicyDetails();
  }, []);

  useEffect(() => {
    if (!user) return;
    if (!Object.keys(customPolicyDetails?.scoringConfig || {}).length) return;
    if (!sharedProfileIds.length) return;

    const loadData = async () => {
      setLoading(true);
      setReportLoading("loading");
      try {
        const tenantRes = await getTenantExtraData(sharedProfileIds);
        const rawTenants = tenantRes?.success_res ?? [];

        // Fetch shared profiles for all tenants concurrently
        const sharedProfiles = await Promise.all(
          sharedProfileIds.map(async (profileId) => {
            const landlordId = user?._id;
            const response = await getSharedProfile(profileId, landlordId);
            return { profileId, data: response?.data || response };
          })
        );

        // Create a map of tenant ID to shared profile data
        const profileMap = {};
        sharedProfiles.forEach(({ profileId, data }) => {
          if (data?.singleSharedProfile?.tenantid?._id) {
            profileMap[data?.singleSharedProfile?.tenantid?._id] =
              data?.singleSharedProfile;
          }
        });
        setSharedProfileMap(profileMap);

        const metaMap = {};
        const guarantorMeta = {};
        const guarantorDataArr = [];

        // Process tenants first
        const processedTenants = await Promise.all(
          rawTenants.map(async (tenant) => {
            // Find the shared profile for this tenant
            const sharedProfile = profileMap[tenant._id];

            const effortRate = getEffortRateForTenant(tenant, sharedProfile);
            const incomeSelection = resolveIncomeSelection(
              tenant?.income_selection,
              tenant?._id
            );
            const tenantIncome = getIncomeForRecord(
              tenant,
              incomeSelection?.tenant_source
            );
            tenant._localIncome = tenantIncome;

            // Process guarantors for this tenant
            const guarantors = tenant?.gurantor_id || [];
            const guarantorIncomeSelection =
              incomeSelection?.[tenant?._id]?.guarantor_sources ??
              incomeSelection?.guarantor_sources;

            const guarantorResults = await Promise.all(
              guarantors.map(async (g) => {
                const guarantorIncomeObj = getCustomIncomeDetails(
                  g,
                  user,
                  _t,
                  guarantorIncomeSelection?.[g?._id],
                  undefined,
                  undefined,
                  deaWorkspace,
                  tenant?.custom_income_sources
                );

                const guarantorIncome = guarantorIncomeObj?.amount ?? 0;
                g._localIncome = guarantorIncome;

                // For capacity report, use tenant's effort rate or default 35%
                const guarantorOnlyEffortRate =
                  guarantorIncome > 0 ? effortRate : 0;

                const gTemp = await getFieldForPolicies(
                  g,
                  user,
                  guarantorOnlyEffortRate,
                  false,
                  deaWorkspace
                );

                const gScoreResult = await calculateFinalScore(
                  customPolicyDetails.scoringConfig,
                  customPolicyDetails.overlayConfig,
                  customPolicyDetails.policyConfig,
                  gTemp
                );

                const guarantorScore = gScoreResult?.finalScore ?? 0;

                // Weight based on score (same logic as LinkedReportOverview)
                let weight = 1;
                if (guarantorScore < 65) {
                  weight = 0.6;
                } else if (guarantorScore <= 80) {
                  weight = 0.75;
                }

                const gErrors = Object.entries(gTemp).filter(
                  ([_, value]) => value?.isHardFail === true
                );

                const label = haveValue(g?.forensic_name)
                  ? g?.forensic_name
                  : haveValue(g?.onfido_user_kyc?.onfido_doc_data?.first_name)
                    ? `${g?.onfido_user_kyc.onfido_doc_data.first_name} ${
                        g?.onfido_user_kyc.onfido_doc_data.last_name ?? ""
                      }`
                    : `${g?.fname ?? "N/A"} ${g?.lname ?? ""}`;

                gTemp.label = label;

                const result = await calculateFinalScore(
                  customPolicyDetails.scoringConfig,
                  customPolicyDetails.overlayConfig,
                  customPolicyDetails.policyConfig,
                  gTemp
                );

                result.errorEntries = gErrors;
                result.isHardFail = gErrors?.length > 0;

                const guarantorTenantData = {
                  documentNumber: getIdentityDetails(g, g?.onfido_user_kyc)
                    ?.value,
                  dob: g?.dob,
                  income: g._localIncome,
                };

                // Store meta for guarantor
                guarantorMeta[label.trim()] = {
                  effortRate: guarantorOnlyEffortRate,
                  income: guarantorIncome,
                };

                guarantorDataArr.push({
                  _id: g._id,
                  data: {
                    ...result,
                    vals: gTemp,
                    _id: g._id,
                    tenantData: guarantorTenantData,
                    label: label,
                    isGuarantor: true,
                    role: "guarantor",
                  },
                });

                return guarantorIncome * weight;
              })
            );

            const temp = await getFieldForPolicies(
              tenant,
              user,
              effortRate,
              false,
              deaWorkspace
            );

            const errorEntries = Object.entries(temp).filter(
              ([_, value]) => value?.isHardFail === true
            );

            const label = haveValue(tenant?.forensic_name)
              ? tenant.forensic_name
              : haveValue(tenant?.onfido_user_kyc?.onfido_doc_data?.first_name)
                ? `${tenant.onfido_user_kyc.onfido_doc_data.first_name} ${
                    tenant.onfido_user_kyc.onfido_doc_data.last_name ?? ""
                  }`
                : `${tenant?.fname ?? "N/A"} ${tenant?.lname ?? ""}`;

            tenant.label = label;
            temp.EFFORT_RATE_SCORE = temp?.EFFORT_RATE_SCORE?.EFFORT_RATE_SCORE;

            const result = await calculateFinalScore(
              customPolicyDetails.scoringConfig,
              customPolicyDetails.overlayConfig,
              customPolicyDetails.policyConfig,
              temp
            );

            result.errorEntries = errorEntries;
            result.isHardFail = errorEntries?.length > 0;

            const tenantDataObj = {
              documentNumber: getIdentityDetails(
                tenant,
                tenant?.onfido_user_kyc
              )?.value,
              dob: tenant?.dob,
              income: tenantIncome,
            };

            // keyed by label (same key buildApplicants uses for `name`)
            metaMap[label.trim()] = { effortRate, income: tenantIncome };

            return {
              tenantId: tenant?._id,
              tenantData: {
                ...result,
                vals: temp,
                _id: tenant?._id,
                tenantData: tenantDataObj,
                label,
                isGuarantor: false,
                role: "tenant",
              },
            };
          })
        );

        const tenantResults = {};
        processedTenants.forEach((pt) => {
          if (pt.tenantId) tenantResults[pt.tenantId] = pt.tenantData;
        });

        const guarantorResults = {};
        guarantorDataArr.forEach((gData) => {
          guarantorResults[gData._id] = gData.data;
        });

        const { result: localReportResult } = computeFinalResult({
          tenantResults,
          selectedTenant: rawTenants,
          customPolicyDetails,
          giveInsurance: false,
          finalEffortRate: 35,
          enabled: false,
          deaWorkspace,
          user,
          lastVerdict: null,
          lastRecommendationFlags: {},
          STATUS_STYLES,
          translate: _t,
          isReport: true,
          guarantorResults: guarantorResults,
        });

        if (localReportResult) {
          setLocalReport(localReportResult);
          setTenantMetaMap(metaMap);
          setGuarantorMetaMap(guarantorMeta);
          setReportLoading("loaded");
        } else {
          setReportLoading("failed");
        }
      } catch (err) {
        console.error("[CapacityReportPage]", err);
        setReportLoading("failed");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [customPolicyDetails, user]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const tenantApplicants = useMemo(
    () =>
      (localReport?.report?.applicants ?? []).filter(
        (a) => a?.role !== "guarantor"
      ),
    [localReport]
  );

  const guarantorApplicants = useMemo(() => {
    const allGuarantors = (localReport?.report?.applicants ?? []).filter(
      (a) => a?.role === "guarantor"
    );
    // Deduplicate guarantors by _id
    const seenIds = new Set();
    return allGuarantors.filter((g) => {
      if (seenIds.has(g._id)) {
        return false;
      }
      seenIds.add(g._id);
      return true;
    });
  }, [localReport]);

  const summaryEffortRate = user?.current_workspace?.default_effort_rate ?? 35;

  const { totalIncome, totalMaxCapacity } = useMemo(() => {
    let inc = 0,
      cap = 0;
    Object.values(tenantMetaMap).forEach(({ effortRate, income }) => {
      inc += income;
      cap += (income * effortRate) / 100;
    });
    return { totalIncome: inc, totalMaxCapacity: cap };
  }, [tenantMetaMap]);

  const overallInsurable = useMemo(() => {
    return tenantApplicants.every(
      (applicant) => applicant?.isInsurable !== false
    );
  }, [tenantApplicants]);

  // ── Pillar block renderer ─────────────────────────────────────────────────
  const renderPillarBlock = (applicant, pillarKey, meta, columnIndex) => {
    const title =
      _t(PILLAR_TITLE_KEYS[pillarKey]) ||
      PILLAR_TITLES[pillarKey]?.[currentLang] ||
      _t(pillarKey);
    const subKeys = PILLAR_SUB_KEYS[pillarKey] ?? [];
    const isAffordability = pillarKey === "incomeAffordability";
    const isDoc = pillarKey === "documentationStrength";
    const pillarBadge = getPillarBadgeFromScore(
      pillarKey,
      applicant?.pillars?.[pillarKey]
    );
    const docSubScore = applicant?.subScores?.documentation;
    const missingDocs = docSubScore?.missingDocs ?? [];

    return (
      <div
        className="linked-report__metric-block"
        key={pillarKey}
        style={{
          gridColumn: columnIndex === 0 ? "1" : "2",
          borderRight: columnIndex === 1 ? "none" : "1px solid #edf0f2",
        }}
      >
        <div className="linked-report__metric-header">
          <h3>{title}</h3>
          {!isAffordability && (
            <span
              className="report-overview__badge"
              style={STATUS_VARIANT_STYLES[pillarBadge.variant]}
            >
              {_t(pillarBadge.labelKey)}
            </span>
          )}
        </div>

        {/* Affordability: first row is computed max capacity */}
        {isAffordability && meta && (
          <div className="linked-report__row">
            <span>{`Max capacity (ER ${meta.effortRate}%)`}</span>
            <span>
              {fmtCurrency((meta.income * meta.effortRate) / 100, currentLang)}
            </span>
          </div>
        )}

        {/* Standard sub-score rows */}
        {subKeys.map((subKey) => {
          const subScore = applicant?.subScores?.[subKey];
          const label =
            SUB_SCORE_TRANSLATIONS[subKey]?.[currentLang] || _t(subKey);
          const value = subScore?.label || noData;
          return (
            <div className="linked-report__row" key={subKey}>
              <span>{label}</span>
              <span className={!subScore?.label ? "linked-report__muted" : ""}>
                {value}
              </span>
            </div>
          );
        })}

        {/* Documentation: label + missing docs list */}
        {isDoc && (
          <>
            <div className="linked-report__row">
              <span>
                {SUB_SCORE_TRANSLATIONS.documentation?.[currentLang] ||
                  _t("documentation")}
              </span>
              <span
                className={!docSubScore?.label ? "linked-report__muted" : ""}
              >
                {docSubScore?.label || noData}
              </span>
            </div>
            {missingDocs.length > 0 && (
              <ul className="linked-report__doc-list">
                {missingDocs.map((doc, i) => {
                  const docName =
                    typeof doc === "string"
                      ? doc
                      : doc?.documentNameTranslated?.[currentLang] ||
                        doc?.documentName ||
                        doc?.label ||
                        "";
                  return docName ? <li key={i}>{docName}</li> : null;
                })}
              </ul>
            )}
          </>
        )}
      </div>
    );
  };

  // ── Tenant card renderer ──────────────────────────────────────────────────
  const renderCard = (applicant) => {
    const initials = getInitials(applicant.name);
    const isGuarantor = applicant?.role === "guarantor";
    const meta = isGuarantor
      ? guarantorMetaMap[applicant.name?.trim()]
      : tenantMetaMap[applicant.name?.trim()];
    const docNo = applicant?.tenantData?.documentNumber || noData;
    const dob = formatDateOfBirth(applicant?.tenantData?.dob, currentLang);
    const incomeLabel = currentLang === "es" ? "Ingresos" : "Income";
    const income = fmtCurrency(applicant?.tenantData?.income, currentLang);
    const isInsurable = applicant?.isInsurable !== false;

    return (
      <article
        className="linked-report__card"
        key={applicant._id || applicant.name}
      >
        {/* Card header */}
        <div className="linked-report__card-header">
          <div className="linked-report__avatar">{initials}</div>
          <div>
            <div className="linked-report__person-name-row">
              <div className="linked-report__person-name">{applicant.name}</div>
              {/* <span
                style={{
                  color: isInsurable ? "#82CF3F" : "#E1BB20",
                  backgroundColor: isInsurable ? "#F2FBE9" : "#FFFBEC",
                }}
                className="report-overview__badge"
              >
                <>
                  {isInsurable ? <img src={"/images/shield.svg"} alt="" /> : ""}
                  {_t(isInsurable ? "insurable" : "notInsurable")}
                </>
              </span> */}
            </div>
            <div className="linked-report__person-meta">
              {`${docNo} · ${dob} · ${incomeLabel}: ${income}`}
            </div>
          </div>
        </div>

        {/* Two-column pillar grid — reuses the existing CSS grid */}
        <div className="linked-report__metrics-grid">
          {LEFT_PILLARS.map((key) =>
            renderPillarBlock(applicant, key, meta, 0)
          )}
          {RIGHT_PILLARS.map((key) =>
            renderPillarBlock(applicant, key, meta, 1)
          )}
        </div>
      </article>
    );
  };

  // ── PDF Download ───────────────────────────────────────────────────────────
  const downloadReportPdf = async () => {
    const container = document.getElementById("divToPrint");
    if (!container) return;

    const hiddenElements = document.querySelectorAll(".no-print");

    try {
      setShowSpinner(true);

      const A4_WIDTH = 595.28;
      const A4_HEIGHT = 841.89;
      const PDF_MARGIN = 24;

      hiddenElements.forEach((el) => (el.style.display = "none"));

      const pdf = new jsPDF({
        orientation: "p",
        unit: "pt",
        format: "a4",
      });

      const canvas = await html2canvas(container, {
        useCORS: true,
        backgroundColor: "#ffffff",
        scale: 2,
        ignoreElements: (element) => element.classList.contains("no-print"),
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.95);

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const scale = Math.min(
        (A4_WIDTH - PDF_MARGIN * 2) / imgWidth,
        (A4_HEIGHT - PDF_MARGIN * 2) / imgHeight
      );

      const renderWidth = imgWidth * scale;
      const renderHeight = imgHeight * scale;

      const x = (A4_WIDTH - renderWidth) / 2;
      const y = PDF_MARGIN;

      pdf.addImage(imgData, "JPEG", x, y, renderWidth, renderHeight);

      pdf.save("capacity-report.pdf");

      setShowSpinner(false);
    } catch (err) {
      console.error("Error capturing element:", err);
      setShowSpinner(false);
    } finally {
      hiddenElements.forEach((el) => (el.style.display = ""));
    }
  };

  // ── States ────────────────────────────────────────────────────────────────
  if (loading) return <Loader />;

  if (reportLoading === "loading") {
    return (
      <div className="linked-report">
        <div className="linked-report__container">
          <div className="risk-report-state-container">
            <div className="risk-report-state-icon risk-report-state-icon--spinning">
              <svg
                className="spinner"
                width="32"
                height="32"
                viewBox="0 0 32 32"
              >
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  fill="none"
                  stroke="#A3EB65"
                  strokeWidth="3"
                  strokeOpacity="0.3"
                />
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  fill="none"
                  stroke="#A3EB65"
                  strokeWidth="3"
                  strokeDasharray="80"
                  strokeLinecap="round"
                >
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 16 16"
                    to="360 16 16"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </circle>
              </svg>
            </div>
            <p className="risk-report-state-title">{_t("generatingReport")}</p>
            <p className="risk-report-state-desc">
              {_t("wereAnalyzingTheApplicationThisMayTakeAFewSeconds")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (reportLoading === "failed") {
    return (
      <div className="linked-report">
        <div className="linked-report__container">
          <div className="risk-report-state-container">
            <div className="risk-report-state-icon risk-report-state-icon--error">
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                stroke="#FF3C3C"
                strokeWidth="2"
              >
                <circle cx="16" cy="16" r="14" />
                <path d="M10 10L22 22M22 10L10 22" />
              </svg>
            </div>
            <p className="risk-report-state-title">
              {_t("failedToLoadReport")}
            </p>
            <p className="risk-report-state-desc">
              {_t(
                "somethingWentWrongWhileGeneratingTheRiskAssessmentPleaseTryAgain"
              )}
            </p>
            <button
              className="risk-report-retry-btn"
              onClick={() => window.location.reload()}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M23 4v6h-6" />
                <path d="M1 20v-6h6" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              {_t("retry")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div className="linked-report">
      <div className="linked-report__container" id="divToPrint">
        {/* Page header */}
        <div className="report-overview__header">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "30px",
            }}
          >
            <img
              src="/images/SkorWhite.svg"
              alt="Skor"
              className="skor-logo"
              style={{ height: "40px" }}
            />

            <button
              className="btnNext no-print linked-report__download-btn"
              disabled={showSpinner}
              onClick={downloadReportPdf}
            >
              {!showSpinner && <img src={DownloadIcon} />}
              {showSpinner ? `${_t("downloading")}...` : _t("downloadReport")}
            </button>
          </div>

          <div className="d-flex gap-4">
            <h1
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: "#7f8c8d",
                lineHeight: "100%",
              }}
            >
              {currentLang === "es"
                ? "Informe de Capacidad"
                : "Capacity Report"}
            </h1>
            <div className="report-overview__meta">
              <div className="report-overview__meta-item">
                <img
                  src="/images/multiple-user.svg"
                  alt=""
                  style={{ height: "10px" }}
                />
                <span style={{ fontSize: 14 }}>
                  {sharedProfileIds.length} {_t("tenantsSelected")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* show applicants names in badges */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            marginTop: "16px",
          }}
        >
          {tenantApplicants?.map((applicant) => (
            <div
              key={applicant._id || applicant.name}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 12px",
                backgroundColor: "#ffffff",
                borderRadius: "20px",
                border: "1px solid #e5e7eb",
              }}
            >
              <div className="linked-report__avatar">
                {getInitials(applicant.name)}
              </div>
              <div className="linked-report__person-name">{applicant.name}</div>
            </div>
          ))}
        </div>

        {/* Summary card */}
        <div className="linked-report__card" style={{ marginTop: 24 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
            }}
          >
            <div className="linked-report__metric-block">
              <div
                className="linked-report__row"
                style={{
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 4,
                }}
              >
                <span
                  style={{ fontSize: 16, fontWeight: 500, color: "#9ca3af" }}
                >
                  {currentLang === "es" ? "Ingresos totales" : "Total income"}
                </span>
                <span
                  style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}
                >
                  {fmtCurrency(totalIncome, currentLang)}
                </span>
              </div>
            </div>
            <div className="linked-report__metric-block">
              <div
                className="linked-report__row"
                style={{
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 4,
                }}
              >
                <span
                  style={{ fontSize: 16, fontWeight: 500, color: "#9ca3af" }}
                >
                  {`Max capacity (ER ${summaryEffortRate}%)`}
                </span>
                <span
                  style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}
                >
                  {fmtCurrency(totalMaxCapacity, currentLang)}
                </span>
              </div>
            </div>
            <div
              className="linked-report__metric-block"
              style={{ borderRight: 0 }}
            >
              <div
                className="linked-report__row"
                style={{
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 4,
                }}
              >
                <span
                  style={{ fontSize: 16, fontWeight: 500, color: "#9ca3af" }}
                >
                  {currentLang === "es" ? "Asegurabilidad" : "Insurability"}
                </span>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {overallInsurable && (
                    <img
                      src="/images/shield.svg"
                      alt=""
                      style={{ width: 16, height: 16 }}
                    />
                  )}
                  <span
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: overallInsurable ? "#82CF3F" : "#E1BB20",
                    }}
                  >
                    {_t(overallInsurable ? "insurable" : "notInsurable")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tenants */}
        <h2 className="linked-report__section-title">{_t("tenants")}</h2>
        {tenantApplicants.map(renderCard)}

        {/* Guarantors */}
        <h2 className="linked-report__section-title">{_t("guarantors")}</h2>
        {guarantorApplicants.length > 0 ? (
          guarantorApplicants.map(renderCard)
        ) : (
          <article className="linked-report__card">
            <div className="linked-report__documentation">
              <div className="linked-report__row">
                <span>{_t("noGuarantorsFound")}</span>
                <span className="linked-report__muted">
                  {currentLang === "es" ? "Sin datos" : "No data"}
                </span>
              </div>
            </div>
          </article>
        )}
      </div>
    </div>
  );
};

export default CapacityReportPage;
